// The sheet's state machine.
//
// One page, one sheet, five modes. The sheet changes mode without closing, so
// the ledger stays visible behind it and a driver never navigates away from
// their own history. This file is the transition map from the spec, written
// out: every edge says what carries across it.
//
// It is a pure reducer on purpose. The rule that matters most here — every
// reversal keeps what was entered — is a property of the transitions, not of
// the rendering, so it is tested without a DOM.

import { currentMonthIndex } from 'src/utils/date/monthPeriods'
import type { MonthPeriod } from 'src/utils/date/monthPeriods'

import { DEFAULT_JOB_TITLE } from '../values'

import { defaultShouldContactEmployer } from './certifications'
import type { EntryDraft, Errors } from './validation'
import {
  isAcceptableDotNumber,
  MESSAGES,
  validateEntry,
  validateFormat,
} from './validation'

export type FlowMode =
  | 'closed'
  | 'question'
  | 'search'
  /** Typing an employer the registry does not have. */
  | 'employer'
  | 'contact'
  | 'form'
  | 'unemployment'
  | 'confirmRemove'

export interface CarrierChoice {
  carrierId?: string | null
  name: string
  dotNumber?: string | null
  city?: string | null
  state?: string | null
  street?: string | null
  /** Trucks on this DOT number, to tell a big carrier's registrations apart. */
  powerUnits?: number | null
  /** We already hold this carrier, so its contacts are known. */
  inDirectory?: boolean | null
}

export interface ContactChoice {
  contactId?: string | null
  name?: string | null
  phone?: string | null
  email?: string | null
  /** Where the value came from. 'existing' is one already on the entry. */
  source: 'known_default' | 'known_chosen' | 'manual' | 'existing'
}

export interface FlowState {
  mode: FlowMode
  /** The entry being built or edited. Never discarded by a reversal. */
  draft: EntryDraft
  /** Existing entry being edited, or null when adding. */
  editingId: string | null
  /** The unaccounted period this was opened from, if any. */
  period: MonthPeriod | null
  /** Preserved so "Change" returns to the results the driver already had. */
  query: string
  carrier: CarrierChoice | null
  contact: ContactChoice | null
  /**
   * The driver opened the contact step themselves, from the form. Only then is
   * it shown for an employer that needs no contact.
   */
  contactByRequest: boolean
  /** Only ever set by a Save that failed. */
  errors: Errors
  /** Format errors raised on blur, before anything is required. */
  touched: Errors
}

export type FlowEvent =
  | { type: 'openCurrentlyEmployed' }
  | { type: 'openNotCurrentlyEmployed' }
  | { type: 'openPeriod'; period: MonthPeriod }
  | { type: 'openEntry'; entry: EntryDraft; id: string }
  | { type: 'answerEmployed'; employed: boolean }
  | { type: 'query'; query: string }
  | { type: 'selectCarrier'; carrier: CarrierChoice }
  | { type: 'enterManually' }
  /**
   * Not a driving or DOT-regulated job: typed like a manual entry, but no
   * carrier was ever going to be found, so it is not a missed pick.
   */
  | { type: 'enterNonDriving' }
  /** What was typed reads as time between jobs; record it as that instead. */
  | { type: 'switchToUnemployment' }
  | { type: 'continueFromEmployer' }
  | { type: 'chooseContact'; contact: ContactChoice }
  /** Choosing to type one instead: the fields start blank, not pre-filled. */
  | { type: 'clearContact' }
  | { type: 'continueFromContact' }
  /**
   * The employer answers verifications only through a platform, so there is
   * nobody for the driver to name. Passes the contact step without asking.
   */
  | { type: 'contactNotNeeded' }
  /** Edit the employer that is already recorded, in the fields it lives in. */
  | { type: 'editEmployer' }
  /** Replace it by picking a different carrier from the registry. */
  | { type: 'searchEmployer' }
  | { type: 'editContact' }
  | { type: 'patch'; patch: Partial<EntryDraft> }
  | { type: 'blur' }
  | { type: 'save' }
  | { type: 'dismiss' }
  | { type: 'requestRemove' }
  | { type: 'cancelRemove' }

export const emptyState = (): FlowState => ({
  mode: 'closed',
  draft: { kind: 'employment' },
  editingId: null,
  period: null,
  query: '',
  carrier: null,
  contact: null,
  contactByRequest: false,
  errors: {},
  touched: {},
})

/** What a Save produced, so the screen can persist it and close the sheet. */
export interface FlowResult {
  state: FlowState
  saved?: { draft: EntryDraft; id: string | null }
  removed?: string
}

const withDraft = (
  state: FlowState,
  patch: Partial<EntryDraft>
): FlowState => ({
  ...state,
  draft: { ...state.draft, ...patch },
})

/**
 * Leaving the registry for an employer the driver types themselves.
 *
 * Straight to a form for the employer itself. Going to the contact step first
 * asks who to call at a company nobody has named yet.
 *
 * A search that turned up nothing carries its query in as the name. An empty
 * search carries nothing — and neither wipes what an entry being edited already
 * holds, which is what made "Search instead" then "Enter manually" erase an
 * employer.
 */
const typedEmployer = (
  state: FlowState,
  { nonDrivingJob }: { nonDrivingJob: boolean }
): FlowState => {
  const typed = state.query.trim()
  // Leaving the registry behind takes its address with it: a carrier's city
  // and DOT number under a name the driver typed is a record that claims
  // something untrue.
  const leavingRegistry = state.draft.carrierSelected === true
  const replacing = Boolean(typed) && typed !== state.draft.companyName
  return {
    ...state,
    mode: 'employer',
    carrier: null,
    contact: replacing ? null : state.contact,
    draft: {
      ...state.draft,
      kind: 'employment',
      companyName: typed || state.draft.companyName || '',
      carrierSelected: false,
      nonDrivingJob,
      // "Truck Driver" is only a sensible default for a driving job
      ...(nonDrivingJob && state.draft.position === DEFAULT_JOB_TITLE
        ? { position: null }
        : {}),
      ...(leavingRegistry
        ? {
            dotNumber: null,
            city: null,
            state: null,
            street: null,
            zipCode: null,
          }
        : {}),
      ...(replacing ? { contactName: null, phone: null, email: null } : {}),
    },
  }
}

export const flowReducer = (
  state: FlowState,
  event: FlowEvent,
  nowMonth: number = currentMonthIndex()
): FlowResult => {
  switch (event.type) {
    // ---- opening ------------------------------------------------------
    case 'openCurrentlyEmployed':
      return {
        state: {
          ...emptyState(),
          mode: 'search',
          // The answer to the question is already known, so the form does not
          // ask again: this entry runs to the present.
          draft: {
            kind: 'employment',
            isCurrent: true,
            endMonth: null,
            position: DEFAULT_JOB_TITLE,
            // Entailed by being current; see toFormExperience
            isDriverTerminated: false,
            // A present employer is the one we do not call by default
            shouldContactEmployer: defaultShouldContactEmployer(true),
          },
        },
      }

    case 'openNotCurrentlyEmployed':
      return {
        state: {
          ...emptyState(),
          mode: 'unemployment',
          // End is now; start is left blank rather than fabricated, because
          // only the driver knows how far back this stretch goes.
          draft: { kind: 'unemployment', endMonth: nowMonth, startMonth: null },
        },
      }

    case 'openPeriod':
      return {
        state: {
          ...emptyState(),
          mode: 'question',
          period: event.period,
        },
      }

    case 'openEntry': {
      // Rebuild the contact from what the entry already holds. Without this the
      // employer card has nothing to show, and a contact recorded earlier is
      // invisible and uneditable the moment the sheet is reopened.
      const { contactName, phone, email } = event.entry
      const held = contactName || phone || email
      return {
        state: {
          ...emptyState(),
          mode: event.entry.kind === 'unemployment' ? 'unemployment' : 'form',
          draft: event.entry,
          editingId: event.id,
          contact: held
            ? {
                name: contactName ?? null,
                phone: phone ?? null,
                email: email ?? null,
                source: 'existing',
              }
            : null,
        },
      }
    }

    // ---- the question -------------------------------------------------
    case 'answerEmployed': {
      const period = state.period
      if (event.employed) {
        return {
          state: {
            ...state,
            mode: 'search',
            // Dates are held for the form rather than written in now: a driver
            // may have had several jobs inside one gap, so the period is a
            // starting point, not the answer.
            draft: {
              ...state.draft,
              kind: 'employment',
              endMonth: period?.end,
              position: state.draft.position ?? DEFAULT_JOB_TITLE,
              // A gap is a job that has ended, until the driver says otherwise
              shouldContactEmployer: defaultShouldContactEmployer(
                state.draft.isCurrent
              ),
            },
          },
        }
      }
      return {
        state: {
          ...state,
          mode: 'unemployment',
          draft: {
            ...state.draft,
            kind: 'unemployment',
            startMonth: period?.start,
            endMonth: period?.end,
          },
        },
      }
    }

    // ---- search -------------------------------------------------------
    case 'query':
      return { state: { ...state, query: event.query } }

    case 'selectCarrier': {
      // A different employer means a different contact. Keeping the previous
      // one attached is how a phone number for company A gets saved against
      // company B and dialled for a verification that makes no sense.
      const replacing = event.carrier.name !== state.draft.companyName
      return {
        state: {
          ...state,
          mode: 'contact',
          contactByRequest: false,
          carrier: event.carrier,
          contact: replacing ? null : state.contact,
          draft: {
            ...state.draft,
            kind: 'employment',
            companyName: event.carrier.name,
            dotNumber: event.carrier.dotNumber,
            // The registry's address replaces whatever was there, rather than
            // leaving one carrier's city under another carrier's name
            city: event.carrier.city,
            state: event.carrier.state,
            street: event.carrier.street,
            zipCode: null,
            carrierSelected: true,
            nonDrivingJob: false,
            ...(replacing
              ? { contactName: null, phone: null, email: null }
              : {}),
          },
        },
      }
    }

    case 'enterManually':
      return { state: typedEmployer(state, { nonDrivingJob: false }) }

    case 'enterNonDriving':
      return { state: typedEmployer(state, { nonDrivingJob: true }) }

    case 'switchToUnemployment': {
      // "N/A" and "Unemployment" typed as an employer are a gap, and a gap
      // recorded as an employer is a verification sent to nobody. The period the
      // sheet was opened from carries across; so does an end of "now" for a
      // sheet opened as currently employed.
      const { draft, period } = state
      return {
        state: {
          ...state,
          mode: 'unemployment',
          carrier: null,
          contact: null,
          errors: {},
          touched: {},
          draft: {
            kind: 'unemployment',
            startMonth: period?.start ?? draft.startMonth ?? null,
            endMonth:
              period?.end ??
              (draft.isCurrent ? nowMonth : draft.endMonth ?? null),
            unemploymentReason: draft.unemploymentReason ?? null,
          },
        },
      }
    }

    case 'continueFromEmployer': {
      // "Good to go" has to mean something. A typed employer needs a name and a
      // location, or the entry reaches Save unable to pass and the driver is
      // sent back to a step they thought they had finished.
      const errors: Errors = {}
      if (!state.draft.companyName?.trim()) {
        errors.companyName = MESSAGES.companyName
      }
      if (!state.draft.city?.trim()) errors.city = MESSAGES.city
      if (!state.draft.state?.trim()) errors.state = MESSAGES.state
      if (!isAcceptableDotNumber(state.draft.dotNumber)) {
        errors.dotNumber = MESSAGES.dotNumber
      }
      if (Object.keys(errors).length > 0) {
        return { state: { ...state, errors } }
      }
      return {
        state: {
          ...state,
          mode: 'contact',
          contactByRequest: false,
          errors: {},
        },
      }
    }

    // ---- contact ------------------------------------------------------
    case 'chooseContact': {
      // Typing clears the complaint about what was typed before — from both
      // sets. `touched` holds the blur-time format messages, and leaving those
      // behind kept an error under a field the driver had already corrected.
      const { phone: _p, email: _e, ...keptErrors } = state.errors
      const { phone: _tp, email: _te, ...keptTouched } = state.touched
      return {
        state: {
          ...state,
          errors: keptErrors,
          touched: keptTouched,
          contact: event.contact,
          draft: {
            ...state.draft,
            // The name travels with the number. It used to be held only in
            // flow state, so anything a driver typed was dropped at Save.
            contactName: event.contact.name,
            phone: event.contact.phone,
            email: event.contact.email,
          },
        },
      }
    }

    case 'clearContact': {
      // Typing a different contact starts from nothing. Leaving the selected one
      // in place pre-filled the fields with somebody else's details, so
      // "enter a different contact" produced the same contact.
      const { phone: _cp, email: _ce, ...keptErrors } = state.errors
      const { phone: _ctp, email: _cte, ...keptTouched } = state.touched
      return {
        state: {
          ...state,
          contact: null,
          errors: keptErrors,
          touched: keptTouched,
          draft: {
            ...state.draft,
            contactName: null,
            phone: null,
            email: null,
          },
        },
      }
    }

    case 'continueFromContact': {
      // Every contact field is optional, so continuing blank is the skip. But a
      // value that is filled in and malformed is not a skip — it is a number we
      // would dial and never reach, and letting it past here means it surfaces
      // two steps later against a field the driver can no longer see.
      const formatErrors = validateFormat(state.draft)
      if (Object.keys(formatErrors).length > 0) {
        return { state: { ...state, errors: formatErrors } }
      }
      return { state: { ...state, mode: 'form', errors: {} } }
    }

    case 'contactNotNeeded':
      // Only from the step itself, and never once the driver asked for it: a
      // late answer from the lookup must not pull them out of fields they
      // opened on purpose. Whatever contact is already held is kept.
      if (state.mode !== 'contact' || state.contactByRequest) return { state }
      return { state: { ...state, mode: 'form', errors: {} } }

    // ---- reversals, none of which discard anything ---------------------
    case 'editEmployer':
      // Editing means adjusting what is recorded, so it opens the fields the
      // employer lives in — including for a carrier that came from the registry,
      // whose details are still the driver's to correct. Searching again is a
      // different intent: replacing this employer with another one.
      return { state: { ...state, mode: 'employer' } }

    case 'searchEmployer':
      // Everything is kept. A driver who opens the registry and changes their
      // mind still has the employer they had.
      return { state: { ...state, mode: 'search' } }

    case 'editContact':
      return { state: { ...state, mode: 'contact', contactByRequest: true } }

    // ---- the form ------------------------------------------------------
    case 'patch': {
      const next = withDraft(state, event.patch)
      // Present or not is a fact about the dates. Changing them re-derives
      // permission to make contact, including over an answer already given.
      const datesChanged =
        next.draft.kind === 'employment' &&
        ('startMonth' in event.patch ||
          'endMonth' in event.patch ||
          'isCurrent' in event.patch)
      const draft = datesChanged
        ? {
            ...next.draft,
            shouldContactEmployer: defaultShouldContactEmployer(
              next.draft.isCurrent
            ),
          }
        : next.draft
      // Clear the complaint on anything the driver has just touched, in both
      // sets; a message that outlives the problem is worse than no message.
      const errors = { ...next.errors }
      const touched = { ...next.touched }
      for (const field of Object.keys(event.patch)) {
        delete errors[field]
        delete touched[field]
      }
      if (datesChanged) {
        delete errors.shouldContactEmployer
        delete touched.shouldContactEmployer
      }
      return { state: { ...next, draft, errors, touched } }
    }

    case 'blur':
      // Format only. Nothing is required until Save, so leaving a field empty
      // on the way past is not an error yet.
      return { state: { ...state, touched: validateFormat(state.draft) } }

    case 'save': {
      const errors = validateEntry(state.draft, nowMonth)
      if (Object.keys(errors).length > 0) {
        return { state: { ...state, errors } }
      }
      return {
        state: { ...emptyState() },
        saved: { draft: state.draft, id: state.editingId },
      }
    }

    // ---- leaving --------------------------------------------------------
    case 'dismiss':
      // The draft is kept by the screen, which offers to resume it. Closing a
      // sheet is not the same as abandoning what was typed into it.
      return { state: { ...state, mode: 'closed' } }

    case 'requestRemove':
      return { state: { ...state, mode: 'confirmRemove' } }

    case 'cancelRemove':
      return {
        state: {
          ...state,
          mode: state.draft.kind === 'unemployment' ? 'unemployment' : 'form',
        },
      }

    default:
      return { state }
  }
}
